import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import CommonDatePicker from '../DatePicker/CommonDatePicker';
import { updateTodo } from '../../api/todoApi';
import '../../css/todo.css';

/**
 * To-Do 수정 모달 (공통)
 *
 * Props:
 *   todo       수정할 To-Do 객체
 *   onClose    모달 닫기 콜백
 *   onUpdated  수정 완료 후 콜백
 */

const DUE_OPTIONS = [
  { value: 'none',     label: '기한 없음' },
  { value: 'today',    label: '오늘' },
  { value: 'tomorrow', label: '내일' },
  { value: 'custom',   label: '날짜 선택' },
];

const PRIORITY_OPTIONS = [
  { value: 'high',   label: '높음' },
  { value: 'normal', label: '보통' },
  { value: 'low',    label: '낮음' },
];

// 등록구분 한글명 매핑
const REF_TYPE_LABEL = { board: '게시판', approval: '전자결재', resource: '자원예약', schedule: '일정', pms_checklist: '프로젝트', pms_comment: '프로젝트', pms_feed: '프로젝트' };

function TodoEditModal({ todo, onClose, onUpdated }) {
  // 상태 선언 등은 return문 이전에만 위치
  const [title,    setTitle]    = useState(todo.title || '');
  const [memo,     setMemo]     = useState(todo.memo || '');
  const [priority, setPriority] = useState(todo.priority || 'high');
  const [dueType,  setDueType]  = useState(todo.due_type || 'none');
  // due_date가 YYYY-MM-DD 문자열이면 그대로, 아니면 Date 객체로 변환
  const [dueDate,  setDueDate]  = useState(
    todo.due_date && typeof todo.due_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(todo.due_date)
      ? todo.due_date
      : (todo.due_date ? new Date(todo.due_date) : '')
  );
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const token = sessionStorage.getItem('token');

  // ESC 닫힘
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // 오버레이 클릭 닫힘
  const overlayRef = React.useRef();
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim()) { setError('제목을 입력해주세요.'); return; }
    if (dueType === 'custom' && !dueDate) { setError('기한일을 선택해주세요.'); return; }
    setError('');
    setLoading(true);
    try {
      // dueDate가 문자열이면 그대로, Date 객체면 YYYY-MM-DD로 변환
      let dueDateStr = null;
      if (dueType === 'custom') {
        if (typeof dueDate === 'string') {
          dueDateStr = dueDate;
        } else if (dueDate instanceof Date && !isNaN(dueDate)) {
          const yyyy = dueDate.getFullYear();
          const mm = String(dueDate.getMonth() + 1).padStart(2, '0');
          const dd = String(dueDate.getDate()).padStart(2, '0');
          dueDateStr = `${yyyy}-${mm}-${dd}`;
        }
      }
      await updateTodo(todo.todo_id, {
        title: title.trim(),
        memo: memo || null,
        priority,
        due_type: dueType,
        due_date: dueDateStr,
      }, token);
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      setError('수정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // Portal로 body에 렌더링, 반드시 하나의 부모 <div>로 감싸기
  return ReactDOM.createPortal(
    <div>
      <div
        className="todo-modal-overlay"
        ref={overlayRef}
        onClick={handleOverlayClick}
        style={{
          position: 'fixed', zIndex: 2200, left: 0, top: 0, width: '100vw', height: '100vh',
          background: 'rgba(33,37,41,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >
        <div
          className="todo-register-layer"
          style={{
            minWidth: 560, maxWidth: 900, minHeight: 0, maxHeight: '92vh',
            background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(15,23,42,0.13)',
            padding: 0, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* 타이틀바 */}
          <div
            className="todo-layer-title-bar"
            style={{background: '#1976f7', color: '#fff', fontWeight: 600, fontSize: 17, padding: '0 24px', height: 44, display: 'flex', alignItems: 'center', borderRadius: '12px 12px 0 0', position: 'relative'}}
          >
            <span style={{flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>To-Do 수정</span>
            <button
              onClick={onClose}
              style={{background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', marginLeft: 8, lineHeight: 1, padding: 0, opacity: 0.85}}
              title="닫기"
              tabIndex={0}
            >×</button>
          </div>

          {/* 내용 스크롤 영역 */}
          <div style={{padding: '12px 12px 0 12px', overflowY: 'auto'}}>
            {/* 등록일/완료일 한 줄에 (제목 위) */}
            {(todo.reg_date || todo.completed_at) && (
              <div className="todo-layer-row" style={{gap: 16, alignItems: 'center', minHeight: 32}}>
                <div style={{display: 'flex', alignItems: 'center', flex: 1, gap: 8, minHeight: 32}}>
                  <label className="todo-layer-label" style={{minWidth: 48, height: 32, display: 'flex', alignItems: 'center'}}>등록일</label>
                  <div style={{minWidth: 0, flex: 1, background: '#f8f9fa', color: '#222', fontSize: 11, fontWeight: 500, lineHeight: '20px', padding: '0 8px', borderRadius: 4, height: 20, display: 'flex', alignItems: 'center'}}>
                    {todo.reg_date ? todo.reg_date.replace('T', ' ').slice(0, 16) : '-'}
                  </div>
                </div>
                {todo.completed_at && (
                  <div style={{display: 'flex', alignItems: 'center', flex: 1, gap: 8, minHeight: 32}}>
                    <label className="todo-layer-label" style={{minWidth: 48, height: 32, display: 'flex', alignItems: 'center'}}>완료일</label>
                    <div style={{minWidth: 0, flex: 1, background: '#f8f9fa', color: '#222', fontSize: 11, fontWeight: 500, lineHeight: '20px', padding: '0 8px', borderRadius: 4, height: 20, display: 'flex', alignItems: 'center'}}>
                      {todo.completed_at ? todo.completed_at.replace('T', ' ').slice(0, 16) : '-'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* [추가] 프로젝트명 (프로젝트 연동 데이터인 경우에만 표시) */}
            {todo.ref_type?.startsWith('pms_') && todo.add_title && (
              <div className="todo-layer-row" style={{alignItems: 'center', minHeight: 32}}>
                <label className="todo-layer-label" style={{display: 'flex', alignItems: 'center', height: 32}}>프로젝트</label>
                <div style={{flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, height: 32}}>
                  <div
                    style={{
                      background: '#f8f9fa',
                      flex: '1 1 0',
                      minWidth: 0,
                      maxWidth: 420,
                      color: '#0d6efd',
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: '20px',
                      padding: '0 8px',
                      borderRadius: 4,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title={todo.add_title}
                  >
                    {todo.add_title}
                  </div>
                </div>
              </div>
            )}

            {/* 제목 */}
            <div className="todo-layer-row" style={{alignItems: 'center', minHeight: 32}}>
              <label className="todo-layer-label" style={{display: 'flex', alignItems: 'center', height: 32}}>{todo.ref_type === 'pms_checklist' || todo.ref_type === 'pms_comment' ? '상세항목' : '제목'}</label>
              <div style={{flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, height: 32}}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{
                    background: '#f8f9fa',
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 420,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: '#222',
                    fontSize: 11,
                    fontWeight: 500,
                    lineHeight: '20px',
                    padding: '0 8px',
                    borderRadius: 4,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  placeholder="To-Do 제목"
                />
                <span
                  className={`badge ${todo.completed_at ? 'bg-success' : 'bg-warning text-dark'}`}
                  style={{marginLeft: 'auto', flexShrink: 0}}
                >
                  {todo.completed_at ? '완료' : '미완료'}
                </span>
              </div>
            </div>

            {/* 등록구분/중요도 한 줄에 50:50 배치 (제목 아래) */}
            <div className="todo-layer-row" style={{display: 'flex', alignItems: 'center', minHeight: 32, gap: 16}}>
              {/* 등록구분(왼쪽, 50%) */}
              <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, minHeight: 32}}>
                <label className="todo-layer-label" style={{display: 'flex', alignItems: 'center', height: 32}}>등록구분</label>
                <div style={{background: '#f8f9fa', color: '#222', fontSize: 11, fontWeight: 500, lineHeight: '20px', padding: '0 8px', borderRadius: 4, height: 20, display: 'flex', alignItems: 'center', minWidth: 0, flex: 1}}>{REF_TYPE_LABEL[todo.ref_type] || todo.ref_type || '-'}</div>
              </div>
              {/* 중요도(오른쪽, 50%) */}
              <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, minHeight: 32}}>
                <label className="todo-layer-label" style={{fontSize: '11px', display: 'flex', alignItems: 'center', height: 32}}>중요도</label>
                <div className="todo-layer-chip-group" style={{display: 'flex', gap: 6, alignItems: 'center', height: 32, minHeight: 32}}>
                  {PRIORITY_OPTIONS.map(opt => {
                    let chipClass = `todo-layer-chip todo-layer-chip--priority-${opt.value}`;
                    if (priority === opt.value) chipClass += ' active';
                    return (
                      <span
                        key={opt.value}
                        className={chipClass}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 32, minWidth: 36, fontSize: 11, fontWeight: 500, verticalAlign: 'middle',
                          background: priority === opt.value ? '#198754' : '#e9ecef',
                          color: priority === opt.value ? '#fff' : '#222',
                          border: 'none',
                          cursor: 'pointer',
                          userSelect: 'none',
                          boxShadow: 'none',
                          pointerEvents: 'auto',
                          padding: '0 8px',
                        }}
                        onClick={() => setPriority(opt.value)}
                      >
                        {opt.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 기한 옵션 */}
            <div className="todo-layer-row todo-layer-row--chips">
              <label className="todo-layer-label">기한</label>
              <div className="todo-layer-chip-group" style={{display: 'flex', gap: 6, alignItems: 'center', height: 32, minHeight: 32}}>
                {DUE_OPTIONS.map((opt) => {
                  let chipClass = 'todo-layer-chip';
                  if (dueType === opt.value) chipClass += ' active';
                  return (
                    <button
                      key={opt.value}
                      className={chipClass}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 32, minWidth: 36, fontSize: 11, fontWeight: 500, verticalAlign: 'middle',
                        background: dueType === opt.value ? '#198754' : '#e9ecef',
                        color: dueType === opt.value ? '#fff' : '#222',
                        border: 'none',
                        cursor: 'pointer',
                        userSelect: 'none',
                        boxShadow: 'none',
                        pointerEvents: 'auto',
                        padding: '0 8px',
                      }}
                      onClick={() => {
                        if (opt.value === 'today') {
                          const today = new Date();
                          const yyyy = today.getFullYear();
                          const mm = String(today.getMonth() + 1).padStart(2, '0');
                          const dd = String(today.getDate()).padStart(2, '0');
                          setDueType('today');
                          setDueDate(`${yyyy}-${mm}-${dd}`);
                        } else if (opt.value === 'tomorrow') {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          const yyyy = tomorrow.getFullYear();
                          const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
                          const dd = String(tomorrow.getDate()).padStart(2, '0');
                          setDueType('tomorrow');
                          setDueDate(`${yyyy}-${mm}-${dd}`);
                        } else if (opt.value === 'custom') {
                          const today = new Date();
                          const yyyy = today.getFullYear();
                          const mm = String(today.getMonth() + 1).padStart(2, '0');
                          const dd = String(today.getDate()).padStart(2, '0');
                          setDueType('custom');
                          setDueDate(`${yyyy}-${mm}-${dd}`);
                        } else if (opt.value === 'none') {
                          setDueType('none');
                          setDueDate('');
                        }
                      }}
                      type="button"
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 날짜 필드: 오늘/내일/날짜선택일 때 항상 표시 + 기한지남 배지 */}
            {(dueType === 'today' || dueType === 'tomorrow' || dueType === 'custom') && (
              (() => {
                // 오늘 날짜 (yyyy-mm-dd)
                const todayStr = new Date().toISOString().slice(0, 10);
                let dateStr = '';
                if (typeof dueDate === 'string') {
                  dateStr = dueDate;
                } else if (dueDate instanceof Date && !isNaN(dueDate)) {
                  const yyyy = dueDate.getFullYear();
                  const mm = String(dueDate.getMonth() + 1).padStart(2, '0');
                  const dd = String(dueDate.getDate()).padStart(2, '0');
                  dateStr = `${yyyy}-${mm}-${dd}`;
                }
                const isOverdue = dateStr && dateStr < todayStr;
                return (
                  <div className="todo-layer-row" style={{alignItems: 'center'}}>
                    <label className="todo-layer-label">날짜</label>
                    {dueType === 'custom' ? (
                      <CommonDatePicker
                        value={
                          typeof dueDate === 'string'
                            ? (dueDate ? new Date(dueDate) : null)
                            : (dueDate instanceof Date && !isNaN(dueDate) ? dueDate : null)
                        }
                        onChange={(date) => {
                          if (date instanceof Date && !isNaN(date)) {
                            const yyyy = date.getFullYear();
                            const mm = String(date.getMonth() + 1).padStart(2, '0');
                            const dd = String(date.getDate()).padStart(2, '0');
                            setDueDate(`${yyyy}-${mm}-${dd}`);
                          } else {
                            setDueDate('');
                          }
                        }}
                        placeholder="날짜 선택"
                        inputClassName="todo-date-input form-control form-control-sm"
                        style={{width: 110, minWidth: 0, display: 'inline-block'}}
                      />
                    ) : (
                      <input
                        type="text"
                        className="todo-date-input form-control form-control-sm"
                        value={typeof dueDate === 'string' ? dueDate : ''}
                        readOnly
                        tabIndex={-1}
                        style={{background: '#f8f9fa', color: '#222', width: 110, minWidth: 0, display: 'inline-block'}} 
                      />
                    )}
                    <div style={{flex: 1, display: 'flex', justifyContent: 'flex-end'}}>
                      {isOverdue && (
                        <span style={{color: '#d32f2f', fontWeight: 700, fontSize: 12, background: '#ffeaea', borderRadius: 4, padding: '2px 10px', height: 24, display: 'flex', alignItems: 'center', marginLeft: 8}}>
                          [기한지남]
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()
            )}

            {/* 메모 */}
            <div className="todo-layer-row">
              <label className="todo-layer-label">메모</label>
              <textarea
                className="form-control form-control-sm"
                style={{height: 100, width: '100%', resize: 'vertical'}} 
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                maxLength={500}
                placeholder="메모 (선택)"
              />
            </div>

            {/* 오류 메시지 */}
            {error && <div className="todo-layer-error">{error}</div>}
          </div>

          {/* 버튼 */}
          <div className="todo-layer-actions" style={{
            padding: '0px 20px 12px 0px',
            minHeight: 65,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}>
            <div style={{display: 'flex', gap: 4, alignItems: 'center', marginRight: '10px'}}>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? '수정 중...' : '수정'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={onClose}>
                취소
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default TodoEditModal;
